import React, { useEffect, useState, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { useHistory } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import {
  createInteractivePageSection,
  updateWidgetProperty,
  toggleEditMode,
  selectPageWidget,
  createPageSegment,
  actionGroup,
  deleteWidgets,
  arrangeWidget,
  clonePage,
  createWidget,
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Frame } from 'scenejs';

import { EditorContext } from '../../index';
import StoryboardContext from 'components/Storyboard/context';
import { Container } from '../core/container';
import { Button, Spinner, Image, Row, Col } from 'react-bootstrap';
import { Background } from 'components/Editor/components/properties/background';

import { Property } from '../../components/properties/';
import Tooltip from "components/Tooltip";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import {
  useChildrenWidgetSelected,
  useContainerSelector,
  useEmptyWidgetSelection,
  useHotspotWidgetSelector,
  useSelectedWidgetSelector,
  useWidgetSelector,
  useWidgetChildrenSelector,
  usePageWidgetSelector,
} from "components/Editor/hooks/useEditorSession";
import { Widget } from "./../../widgets/core";
import Portal from "components/Portal";
import { BackgroundView } from "../background";
import { PAGE, SECTION, SECTION_COLUMN, TEXT } from "constants/widgetTypes";
import { useLocation, useRouteMatch } from "react-router-dom";
import { getMediaInfo } from "actions/pageHealth";
import { HeaderContext } from "pages/pages/components/header";
import { createPage } from "actions/pages";
import { getBaseUrl } from "services/util";
import { INTERACTIVE_CONTENT_TYPES, SEGMENT_STORYBOARDS, SLIDE_STORYBOARDS, STORYBOARD_TYPES } from "constants/storyboardType";
import smartGatingIcon from "../../../../assets/images/smartGatingBorder.svg";
import { desktopToMobileOptimize, mobileLayoutOptimize } from "actions/mobileOptimization";

import styles from "./page.module.scss";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;


export const PageWidgetContext = React.createContext({
  onResize: () => { },
});

const PageWidgetComponent = function ({
  className,
  style,
  widgetProps,
  widgetRef,
  children,
}) {
  const ref = widgetRef;

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const headerContext = React.useContext(HeaderContext);

  const editor = editorContext.editorSession.id;
  const editorWidgetViewer = document.getElementsByClassName(
    "editor_widget_viewer"
  )[0];
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`),
  );
  const pages = useSelector((state) => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    var pages = widgets.filter((item) => item.type === PAGE);
    pages = _.orderBy(pages, "order", "desc");
    return pages;
  });
  
  const previousBreakpointRef = useRef(breakpoint);
  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, "type");
  const storyboardId = _.get(storyboard, 'id');
  const background = _.get(widgetProps, "properties.desktop.background");
  const entityType = useSelector((state) => _.get(state, `${editor}.present.entityType`));
  const isOptimizing = useSelector(state => _.get(state, `${editor}.present.mobileOptimization.pageWidgets.${widgetProps.uid}.optimizing`, false));
  const view = useSelector(state => {
    const mobile = _.get(
        state,
        `${editor}.present.mobileOptimization.pageWidgets.${widgetProps.uid}.view`,
        "desktop"
      ) === "mobile";
  
    return mobile ? "mobile" : "desktop";
  });  

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");

  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const selectedWidget = useSelectedWidgetSelector(editor)?.[0];
  const visibleWitdh = _.get(
    widgetProps,
    `properties.${breakpoint}.container.minWidth`
  );
  const widgetHeight =
    parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.height`)) || 0;

  const hotspotWidget = useHotspotWidgetSelector(editor, breakpoint);
  const hotspotWidgetParent = useWidgetSelector(
    editor,
    hotspotWidget && hotspotWidget?.parent
  );
  const hotspotVisibility = _.get(
    hotspotWidgetParent,
    `properties.${breakpoint}.visible`
  );

  const hotspotContainer = usePageWidgetSelector(editor, hotspotWidgetParent?.uid)
  const tools = useSelector((state) => _.get(state, `${editor}.present.tools`));
  const selected = useSelector((state) => {
    const selectedPage = _.get(state, `${editor}.present.selectedPage`, null);
    const selection = _.get(state, `${editor}.present.selected`, []);
    return selectedPage === id || selection.includes(id);
  });

  const offset = breakpoint === "desktop" ? 100 : 100;

  const [heights, setHeights] = useState({
    desktop: widgetHeight,
    mobile: widgetHeight,
  });

  const showToolbar = useEmptyWidgetSelection(editor);

  const widgetSelectionUids = useSelector(
    (state) => _.get(state, `${editor}.present.selected`) || []
  );
  const selectedPageInEditor = useSelector((state) =>
    _.get(state, `${editor}.present.selectedPage`)
  );

  const isValidStorybaord = SLIDE_STORYBOARDS.includes(storyboardType);
  const shouldShowOptimizeLoader = breakpoint === "mobile" && isOptimizing;
  const isSelectedWidgetValid =
    useContainerSelector(editor, _.get(selectedWidget, "uid"))?.type === PAGE &&
    _.get(selectedWidget, "type") !== SECTION;

  const [pageWidgetContext, setPageWidgetContext] = useState({
    onResize: () => { },
  });
  const childrenVisibility = useWidgetChildrenSelector(editor, id)
  ?.map(c => _.get(c, `properties.${breakpoint}.visible`, true)).join(",");

  var widgetStyle = {
    ...style,
    width: `${visibleWitdh}px`,
    minHeight: `50px`,
  };

  useEffect(() => {
    if ((storyboardType === "INTERACTIVE_CONTENT" || isValidStorybaord || entityType === "PALETTE_COMPONENT")) {
      handleResize();
    }
  }, [childrenVisibility]);

  useEffect(() => {
    // Resize the page height widget when the selected widget is deleted
    if (!selectedWidget && (storyboardType === "INTERACTIVE_CONTENT" || isValidStorybaord || entityType === "PALETTE_COMPONENT")) {
      handleResize();
    }
  }, [storyboardType, selectedWidget]);

  useEffect(() => {
    if (headerContext.optimizeLayout) {
      handleResize();
    }

  }, [headerContext.optimizeLayout])

  React.useEffect(() => {
    setPageWidgetContext({
      onResize: handleResize,
    });
  }, []);

  React.useEffect(() => {
    const breakpointChanged = previousBreakpointRef.current !== breakpoint;
    previousBreakpointRef.current = breakpoint;

    if (breakpointChanged) return;

    const height = heights[breakpoint];
    if (!height) return;

    const layout = _.get(widgetProps, `properties.${breakpoint}.layout`) || {};

    const normalizedHeight = height.toString().endsWith("px")
      ? height
      : `${height}px`;

    if (layout?.height === normalizedHeight) return;

    dispatch(
      updateWidgetProperty(editor, id, breakpoint, {
        layout: {
          ...layout,
          height: normalizedHeight,
        },
      }),
    );
  }, [heights, breakpoint]);

  const handleResize = () => {
    let calculatedHeight = 0;
    const parentRect = widgetRef.current.getBoundingClientRect();

    const editorWidgetViewer = document.getElementsByClassName(
      "editor_widget_viewer"
    )[0];

    const widgets = editorWidgetViewer.querySelectorAll(
      `#${id} .widget[data-widget-id]`
    );
    Array.from(widgets).forEach((element) => {
      const rect = element.getBoundingClientRect();
      calculatedHeight = Math.max(calculatedHeight, rect.bottom - parentRect.top);
    });
  
    calculatedHeight = Math.max(50, calculatedHeight);
    setHeights(prev => ({ ...prev, [breakpoint]: `${calculatedHeight}px` }));
  };

  const selectedWidgetLayout = useSelector((state) => {
    if (!selectedWidget) return null;
    return _.get(selectedWidget, `properties.${breakpoint}.layout`);
  });
  
  const layoutDependency = useMemo(() => {
    if (!SEGMENT_STORYBOARDS.includes(storyboardType)) return selectedWidgetLayout;
    if (!selectedWidget) return null;
  
    if (_.get(selectedWidget, 'type') === TEXT) {
      const el = document.getElementById(_.get(selectedWidget, 'uid'));
      if (el) return el.getBoundingClientRect().height;
      return selectedWidgetLayout?.height;
    }
    return selectedWidgetLayout?.height;
  }, [selectedWidgetLayout, storyboardType, selectedWidget]);

  const handleResizeRef = useRef(handleResize);
  handleResizeRef.current = handleResize;

  useEffect(() => {
    if (!SEGMENT_STORYBOARDS.includes(storyboardType)) return;
    if (widgetSelectionUids.length === 0) return;
    if (id !== selectedPageInEditor) return;

    const pageEl = document.getElementById(id);
    if (!pageEl) return;

    const pageRect = pageEl.getBoundingClientRect();
    let maxBottom = 0;

    pageEl.querySelectorAll(".widget[data-widget-id]").forEach((node) => {
      const wid = node.getAttribute("data-widget-id");
      if (!wid) return;
      const widgetEl = document.getElementById(wid);
      if (!widgetEl) return;
      const widgetRect = widgetEl.getBoundingClientRect();
      const bottom = widgetRect.bottom - pageRect.top;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });

    if (maxBottom > pageRect.height) {
      setHeights(prev => ({ 
        ...prev, 
        [breakpoint]: `${maxBottom}px` 
      }));
    }
  }, [selectedPageInEditor, storyboardType, id, layoutDependency]);

  const handleSelectPage = (e) => {
    if (selected) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    dispatch(selectPageWidget(editor, id));
  };

  const handleAddSection = () => {
    const desktopWidgetHeight = parseInt(_.get(widgetProps, `properties.desktop.layout.height`)) || 0;
    const mobileWidgetHeight = parseInt(_.get(widgetProps, `properties.mobile.layout.height`)) || 0;

    let sectionWidget = {
      name: "Section",
      type: SECTION,
      properties: {
        "desktop": {
          "visible": true,
          "layout": {
            "transform": {
              "translate": "0px, 0px",
              "rotate": "0deg"
            },
            "width": null,
            "height": `${desktopWidgetHeight}px`
          },
          "stretched": true,
          "margin": 0,
          "padding": 0,
          "columnSpacing": 0,
          "background": {
            "type": "color",
            "color": {
              "type": "solid",
              "r": 242,
              "g": 242,
              "b": 242,
              "a": 1
            },
            "media": null,
            "opacity": 0,
            "scale": null,
            "position": null
          },
          "shadow": {
            "color": {
              "type": "solid",
              "r": 255,
              "g": 255,
              "b": 255,
              "a": 1
            },
            "axes": {
              "x": "0",
              "y": "0",
              "b": "0"
            },
            "show": false
          }
        },
        "mobile": {
          "visible": true,
          "layout": {
            "transform": {
              "translate": "0px, 0px",
              "rotate": "0deg"
            },
            "width": "100%",
            "height": `${mobileWidgetHeight}px`
          },
          "stretched": false,
          "margin": 0,
          "padding": 0,
          "columnSpacing": 0,
          "background": null,
          "shadow": {
            "color": {
              "type": "solid",
              "r": 255,
              "g": 255,
              "b": 255,
              "a": 1
            },
            "axes": {
              "x": "0",
              "y": "0",
              "b": "0"
            },
            "show": false
          }
        }
      },
      widgets: _.times(1).map((index) => {
        return {
          type: SECTION_COLUMN,
          name: "Column",
          properties: {
            "desktop": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px, 0px"
                },
                "width": `${100 / 1}%`,
                "height": null
              },
              "container": {
                "minWidth": 1200 / 1,
                "alignment": "center"
              },
              "alignment": "center",
              "background": null
            },
            "mobile": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px, 0px"
                },
                "width": "100%",
                "height": `${mobileWidgetHeight}px`
              },
              "container": {
                "minWidth": 375,
                "alignment": null
              },
              "alignment": "center",
              "background": null
            }
          }
        }
      })
    };

    dispatch(createWidget(editor, [sectionWidget], id));
  }

  const handlePaletteClick = () => {
    const paletteBtn = document.getElementById("button-palette");
    if (paletteBtn) {
      paletteBtn.click();
    }
  };

  return (
    <>
      <PageWidgetContext.Provider value={pageWidgetContext}>
        {(storyboardType === "INTERACTIVE_CONTENT" || SLIDE_STORYBOARDS.includes(storyboardType) || entityType === "PALETTE_COMPONENT") && (
          <Container widgetProps={widgetProps} widgetRef={widgetRef}>
            <div
              id={id}
              ref={ref}
              className={cx(className, styles.page, "cs_page_root", {
                [styles.mobile]: breakpoint === "mobile",
                [styles.optimizing]: isOptimizing
              })}
              data-storyboard-type={storyboardType}
              data-widget-id={id}
              data-widget-type={type}
              data-container={true}
              data-selectable={true}
              style={widgetStyle}
            >
              {shouldShowOptimizeLoader && storyboardType === "INTERACTIVE_CONTENT" && (
                <>
                <div className={styles.overlay}></div>
                <OptimizeLoader editor={editor} parent={widgetProps.uid} view={view} />
                </>
              )}
              {background && editorWidgetViewer && (
                <Portal mountNode={editorWidgetViewer}>
                  <BackgroundView
                    className={styles.pageBackground}
                    background={background}
                  />
                </Portal>
              )}
              {children}

              {hotspotWidget && hotspotVisibility && (
                <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
              )}

              {!editorContext.disabled && showToolbar && (
                <PageWidgetDesktopAction
                  editor={editor}
                  storyboardType={storyboardType}
                  breakpoint={breakpoint}
                  storyboardId={storyboardId}
                  isOptimizing={isOptimizing}
                  {...widgetProps}
                />
              )}
            </div>
          </Container>
        )}
        {SEGMENT_STORYBOARDS.includes(storyboardType) &&
          _.get(widgetProps, 'properties.desktop.visible', true) !== false && (
          <Container widgetProps={widgetProps} widgetRef={widgetRef}>
            <div
              id={`page-${id}`}
              className={cx(
                styles.pageWidgetContainer,
                {
                  [styles.selected]: selected,
                },
                { [styles.mobileContainer]: breakpoint === "mobile" },
                { [styles.optimizing]: isOptimizing }
              )}
              style={{ height: `${widgetHeight}px`, minHeight: "50px" }}
              onClick={handleSelectPage}
              > 
              <div id={`hover-${id}`} className={cx(styles.hoverOverlay,{[styles.hide]: widgetHeight < 150 || breakpoint === "mobile"})}>
                {children.length == 0 &&
                  <div className={styles.emptyContainer}>
                    <div className={styles.empty}>
                      <div className={styles.buttonGroup}>
                        <button className={styles.iconButton} onClick={handleAddSection}>
                          <i className={cx('cleverstory-add-segment-element')} />
                          <span>Add Section</span>
                        </button>
                        <button className={styles.iconButton} onClick={handlePaletteClick}>
                          <i className={cx('cleverstory-add-segment-element')} />
                          <span>Add Elements</span>
                        </button>
                      </div>
                    </div>
                  </div>
                }
              </div>
              {background && editorWidgetViewer && (
                <BackgroundView
                  className={styles.pageBackground}
                  background={background}
                />
              )}
              {shouldShowOptimizeLoader && 
                <>
                  <div className={styles.overlay}></div>
                  <OptimizeLoader editor={editor} parent={widgetProps.uid} view={view} />
                </>
              }
              <div
                id={id}
                ref={ref}
                className={cx(className, styles.page, "cs_page_root", {
                  [styles.mobile]: breakpoint === "mobile",
                })}
                data-storyboard-type={storyboardType}
                data-widget-id={id}
                data-widget-type={type}
                data-container={true}
                data-selectable={true}
                style={widgetStyle}
              >
                {children}

                {hotspotWidget && hotspotVisibility && hotspotContainer?.uid === id &&  (
                  <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
                )}
              </div>
              {!editorContext.disabled && (
                <PageWidgetDesktopAction
                  editor={editor}
                  showToolbar={(selected && _.size(selectedWidget) == 0)}
                  storyboardType={storyboardType}
                  breakpoint={breakpoint}
                  storyboardId={storyboardId}
                  isOptimizing={isOptimizing}
                    {...widgetProps}
                  />
                )}

                {!editorContext.disabled &&
                  selected &&
                  _.size(selectedWidget) == 0 && (
                    <PageControllerAction
                      pageId={id}
                      pages={pages}
                      editor={editor}
                      storyboardType={storyboardType}
                      breakpoint={breakpoint}
                    />
                  )
                }
              </div>
            </Container>
          )}
      </PageWidgetContext.Provider>

      {!editorContext.disabled && breakpoint === 'desktop' && INTERACTIVE_CONTENT_TYPES.includes(storyboardType) &&
        <>
          <CreatePageButton
            editorSession={editor}
            style={{
              right: `calc((100% - ${visibleWitdh}px)/2)`,
              top: `50%`
            }}
            storyboard={storyboard}
          />
        </>
      }

      {tools.gridlines && (storyboardType === "INTERACTIVE_CONTENT" || entityType === "PALETTE_COMPONENT") && (
        <>
          <div
            className={cx(styles.boundary, styles.top, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              top: offset,
            }}
          />
          <div
            className={cx(styles.boundary, styles.bottom, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              top: `${widgetHeight + offset}px`,
            }}
          />

          <div
            className={cx(styles.boundary, styles.left, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              left: `calc((100% - ${visibleWitdh}px)/2)`,
              height: `${widgetHeight + 300}px`,
            }}
          />
          <div
            id="forGuideLineWidth"
            style={{
              width: `calc((100% - ${visibleWitdh}px)/2)`,
              left: 0,
              position: "absolute",
            }}
          ></div>
          <div
            className={cx(styles.boundary, styles.right, {
              [styles.hideBoundary]:
                childrenSelection.selected && !isSelectedWidgetValid,
            })}
            style={{
              right: `calc((100% - ${visibleWitdh}px)/2)`,
              height: `${widgetHeight + 300}px`,
            }}
          />
        </>
      )}
    </>
  );
};

const pageWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef", "children"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const PageWidget = React.memo(PageWidgetComponent, pageWidgetEquals);

const FrameSkeletonLoader = () => {
  const LEFT_MESSAGES = [
    "Loading your data...",
    "Please wait...",
    "Almost there...",
    "Just a moment..."
  ];
  const RIGHT_PARAGRAPH = "We're setting up your workspace. This won't take long...";
  const [leftIndex, setLeftIndex] = useState(0);
  const [rightText, setRightText] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  // Left carousel animation
  useEffect(() => {
    const interval = setInterval(() => {
      setLeftIndex((prev) => (prev + 1) % LEFT_MESSAGES.length);
    }, 2500); // Change message every 2.5s

    return () => clearInterval(interval);
  }, []);

  // Right typewriter animation
  useEffect(() => {
    let currentIndex = 0;
    let timeout;

    const type = () => {
      if (currentIndex < RIGHT_PARAGRAPH.length) {
        setRightText(RIGHT_PARAGRAPH.slice(0, currentIndex + 1));
        currentIndex++;
        timeout = setTimeout(type, 100); // 100ms per letter
      } else {
        // Finished typing, wait 2s then restart
        timeout = setTimeout(() => {
          setRightText("");
          currentIndex = 0;
          type();
        }, 2000);
      }
    };

    type();

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className={styles.loaderContainer}>
      <div className={styles.contentWrapper}>
        <Row className={styles.contentRow}>
          <Col className={styles.leftAnimationContent} sm={6}>
            <div className={styles.box}>
              <div className={styles.carouselContainer}>
                {LEFT_MESSAGES.map((msg, i) => (
                  <div
                    key={i}
                    className={styles.carouselItem}
                    style={{
                      transform: `translateY(${(i - leftIndex) * 100}%)`
                    }}
                  >
                    {msg}
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col className={styles.rightAnimationContent} sm={6}>
            <div className={styles.box}>
              <div className={styles.typewriterContainer}>
                <p className={styles.typewriterText}>
                  {rightText}
                  <span className={styles.cursor}>|</span>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

const PageWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isPaletteComponentEditor =
    location.pathname.includes("paletteComponent");
  const breakpoint = _.get(props, "breakpoint");

  const [view, setView] = useState("style");
  const isSegment = SEGMENT_STORYBOARDS.includes(props.storyboardType);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const selectedWidget = useSelectedWidgetSelector(editorSession)?.[0];
  const aiMobileOptimizationEnabled = useSelector(state => _.get(state, 'workspaces.workspace.item.aiMobileOptimizationEnabled', false));

  const desktopToMobileOptimized = useSelector(state => _.get(state, `${editorSession}.present.mobileOptimization.pageWidgets.${props.uid}.desktopToMobileOptimized`, false));

  const storyboardId = _.get(props, 'storyboardId') || '';
  const isOptimizing = _.get(props, 'isOptimizing') || false;

  const background = _.get(props, "properties.desktop.background", null);
  const locked = _.get(props, "properties.desktop.locked", null);

  const selectedPage = useSelector((state) => {
    return _.get(state, `${editorSession}.present.selectedPage`);
  });

  const showSettings = isSegment ? selectedPage && _.size(selectedWidget) == 0 && props.uid === selectedPage : true;

  useEffect(() => {
    if (selectedPage === null) {
      return;
    }

    if (id !== selectedPage && view === "background") {
      closePopup();
    }
  }, [selectedPage]);

  useEffect(() => {
    if (breakpoint === 'mobile' && view === 'background') {
      closePopup();
    }
  }, [breakpoint])

  const id = _.get(props, "uid");
  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  const handleAddSegment = (position) => {
    if (!editorSession) {
      return;
    }

    dispatch(createPageSegment(editorSession, id, position));
  };

  const handleAIOptimization = () => {
    if (isOptimizing) return;

    if (desktopToMobileOptimized){
      dispatch(mobileLayoutOptimize(storyboardId, props.id, editorSession, props));
    }
    else {
      dispatch(desktopToMobileOptimize(breakpoint, storyboardId, props.id, editorSession, props));
    }
  };

  return (
    <div id={id} className={cx(styles.widgetAction)}>
      {!isPaletteComponentEditor && breakpoint == "desktop" && showSettings && !locked && (
        <Property.Action
          className={cx(styles.textButton, styles.propertyAction, {
            [styles.hideAction]:
              props.storyboardType !== "INTERACTIVE_CONTENT" &&
              id !== selectedPage,
          })}
          onClick={() => changeView("background")}
        >
          Change Background
        </Property.Action>
      )}

      {!isPaletteComponentEditor && breakpoint === "mobile" && showSettings && !locked && !isOptimizing && aiMobileOptimizationEnabled && (
        <Property.Action
          className={cx(styles.textButton, styles.propertyAction, {
            [styles.hideAction]: !SEGMENT_STORYBOARDS.includes(props.storyboardType)
          })}
          onClick={handleAIOptimization}
        >
          <div className={styles.aiIconSection}>
            <Image className={styles.pulseImage} src={smartGatingIcon} width={20} height={20} />
            <Image className={styles.pulseImage2} src={smartGatingIcon} width={20} height={20} />
          </div>
        </Property.Action>
      )}

      { isSegment && ( isSegment ? selectedPage == id : (!isPaletteComponentEditor)) &&
        <TruncatedTextWithTooltip
          className={cx(styles.title)}
          content={props.name}
          maxWidth={150}
          placement="top"
        >
          {props.name}
        </TruncatedTextWithTooltip>
      }

      {!editMode && view === "background" && (
        <Background.Dialog
          id={id}
          value={background}
          onChange={(value) => {
            dispatch(
              updateWidgetProperty(editorSession, id, "desktop", {
                background: value,
              })
            );
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      <AddSegmentButtonComponent
        pageId={id}
        active={props.showToolbar}
        handleAddSegment={handleAddSegment}
        breakpoint={breakpoint}
      />
    </div>
  );
}

const OptimizeLoader = ({ view }) => {
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!wrapperRef.current) return;
  
    const baseHeight = 406;
  
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const parentHeight = entry.contentRect.height;
        const newScale = Math.min(parentHeight / baseHeight, 1);
        setScale(newScale);
      }
    });
  
    observer.observe(wrapperRef.current);
  
    return () => {
      observer.disconnect();
    };
  }, []);

  const content =
    view === "desktop"
      ? {
          title: "Optimizing Layout...",
          description:
            "Our cleverstory AI is transforming your desktop layout for mobile. Hang tight for a few more seconds!",
        }
      : {
          title: "Fine-tuning Layout...",
          description:
            "Our cleverstory AI is making quick mobile adjustments. This will only take a moment!",
        };

  return (
    <div className={styles.loaderWrapper} ref={wrapperRef}>
      <div className={styles.scaler} style={{ transform: `scale(${scale})` }}>
        <div className={styles.skeletelContainer}>
          <div className={cx(styles.skeleton, styles.title)}></div>
          <div className={styles.wrapper}>
            <div className={cx(styles.skeleton, styles.lineShort)}></div>
            <div className={cx(styles.skeleton, styles.title)}></div>
          </div>
          <div className={cx(styles.skeleton, styles.image)}></div>
          <div className={styles.buttonWrapper}>
            <div className={cx(styles.skeleton, styles.button)}></div>
          </div>
          <div className={styles.contentWrapper}>
            <video
              className={styles.optimizeLoader}
              loop
              autoPlay
              playsInline
              muted
            >
              <source src="/images/OptimizationLoader.webm" type="video/webm" />
            </video>
            <div className={styles.content}>
              <span className={styles.heading}>{content.title}</span>
              <span className={styles.description}>{content.description}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PageControllerAction = ({
  pageId,
  storyboardType,
  editor,
  breakpoint,
  pages,
}) => {
  const dispatch = useDispatch();

  const selectedPage = useSelector((state) =>
    _.get(state, `${editor}.present.selectedPage`)
  );

  if (breakpoint !== "desktop" || storyboardType == "INTERACTIVE_CONTENT") {
    return;
  }

  let currentIndex = pages.findIndex((item) => item.uid === pageId);
  const currentPage = pages[currentIndex];
  const visible = _.get(currentPage, 'properties.desktop.visible', true);
  const locked = _.get(currentPage, 'properties.desktop.locked', false);
  const visiblePageCount = pages.filter(p => _.get(p, 'properties.desktop.visible', true)).length;
  const canHideFrame = !visible || visiblePageCount >= 2;
  const canDeleteFrame = _.size(pages) >= 2 && (!visible || visiblePageCount >= 2);

  const handleMoveDown = (e) => {
    e.stopPropagation();

    if (currentIndex == _.size(pages) - 1) {
      return;
    }

    dispatch(arrangeWidget(editor, pages[currentIndex], currentIndex + 1));
    scrollSegmentToView(pageId);
  };

  const handleMoveUp = (e) => {
    e.stopPropagation();

    if (currentIndex === 0) {
      return;
    }

    dispatch(arrangeWidget(editor, pages[currentIndex], currentIndex - 1));
    scrollSegmentToView(pageId);
  };

  const scrollSegmentToView = (pageId) => {
    const el = document.getElementById(`page-${pageId}`);
    const container = document.querySelector(".editor_widget_scroll_container");

    if (el && container) {
      setTimeout(() => {
        container.scrollTo({
          top: el.offsetTop,
          behavior: "smooth",
        });
      }, 100);
    }
  }

  const onDelete = () => {
    let nextPage = null;

    if (!canDeleteFrame) {
      return;
    }

    nextPage =
      currentIndex == _.size(pages) - 1
        ? pages[currentIndex - 1]
        : pages[currentIndex + 1];

    actionGroup.start();
    dispatch(deleteWidgets(editor, [pageId]));
    dispatch(selectPageWidget(editor, nextPage?.uid));
    actionGroup.end();
  };

  const cloneSegment = (e) => {
    e.stopPropagation();

    dispatch(clonePage(editor, pageId, 'segment'));
  };

  const handleToggleVisibility = (e) => {
    e.stopPropagation();

    if (locked || !canHideFrame) {
      return;
    }

    const nextVisible = !visible;

    actionGroup.start();
    dispatch(
      updateWidgetProperty(editor, pageId, 'desktop', {
        visible: nextVisible,
      })
    );

    if (!nextVisible && selectedPage === pageId) {
      const nextPage =
        currentIndex === _.size(pages) - 1
          ? pages[currentIndex - 1]
          : pages[currentIndex + 1];
      dispatch(selectPageWidget(editor, nextPage?.uid));
    }

    actionGroup.end();
  };

  return (
    <div className={styles.sliderControl}>
      <div className={styles.propertyAction}>
        <Property.Action
          onClick={handleMoveUp}
          disabled={currentIndex == 0}
          className={cx(styles.iconButton, styles.previous)}
          tooltip="Move up"
        >
          <i className={cx("cleverstory-back")} />
        </Property.Action>
        <Property.Action
          onClick={handleMoveDown}
          disabled={currentIndex == _.size(pages) - 1}
          className={cx(styles.iconButton, styles.next)}
          tooltip="Move down"
        >
          <i className={cx("cleverstory-back")} />
        </Property.Action>
        <Property.Divider className={styles.divider} />
        <Property.Action
          onClick={cloneSegment}
          className={styles.iconButton}
          tooltip="Clone"
        >
          <i className={cx("cleverstory-clone")} />
        </Property.Action>
        <Property.Divider className={styles.divider} />
        <Property.Action
          disabled={locked || !canHideFrame}
          onClick={handleToggleVisibility}
          className={styles.iconButton}
          tooltip={
            locked
              ? "Frame is locked"
              : !canHideFrame
                ? "At least one frame must remain visible"
                : visible
                  ? "Hide frame"
                  : "Show frame"
          }
        >
          <i
            className={cx({
              "cleverstory-view": visible,
              "cleverstory-hide": !visible,
            })}
          />
        </Property.Action>
        <Property.Action
          disabled={!canDeleteFrame}
          onClick={onDelete}
          className={styles.iconButton}
          tooltip={
            !canDeleteFrame
              ? "At least one frame must remain visible"
              : "Delete"
          }
        >
          <i className={cx("cleverstory-trash")} />
        </Property.Action>
      </div>
    </div>
  );
};

const CreatePageButton = ({ editorSession, style, storyboard }) => {
  const dispatch = useDispatch();
  const pages = useSelector(state => _.get(state, 'pages.items', []));
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const handleCreate = () => {
    if (storyboard.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2) {
      setLoading(true);
      const lastOrder = _.get(_.maxBy(pages, "order"), "order", 0);

      const request = {
        name: `Section ${_.size(pages) + 1}`,
        storyboard: storyboard.id,
        order: lastOrder + 1,
      };

      dispatch(
        createPage(request, (response) => {
          setLoading(false);
          history.push(`${baseUrl}/pages/${response.id}`);
        })
      );
      return;
    }
    dispatch(createInteractivePageSection(editorSession, 0));
  };

  return (
    <div className={styles.createPageButton} style={style}>
      <Tooltip placement='left' content={loading ? 'Adding Page': 'Add Page'}>
        <Button className={cx({ [styles.noAction]: loading })} variant="primary" disabled={loading} onClick={handleCreate}>
          {loading ? <Spinner className={styles.dot} animation="border" size="sm"/> : <i className={cx(styles.icon, "cleverstory-plus")} />}
        </Button>
      </Tooltip>
    </div>
  );
};

PageWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: [],
    onResize: (widget, layout) => {
      const frame = new Frame(layout);

      if (frame.has("transform", "translate")) {
        frame.set("transform", "translate", "0px, 0px");
      }

      const rect = getBoundingRectFromLayout(layout);
      const height = Math.max(50, rect.height);

      frame.set("height", `${height}px`);

      return frame.get();
    },
  };
};

const PageWidgetActions = ({ editor, breakpoint, props }) => {
  if (breakpoint === "mobile") {
    return;
  }

  return (
    <PageWidgetDesktopAction
      editor={editor}
      breakpoint={breakpoint}
      {...props}
    />
  );
};

PageWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, "properties.desktop.background.media.url", "");
  if (!url) {
    return {
      size: 0,
      type: "image",
    };
  }
  return {
    size: (await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache)) || 0, // MB
    type: "image",
  };
};

PageWidget.processRequest = (widget) => {
  var properties = widget.properties;

  var socialIcon = _.get(properties, "desktop.social.icon", null);
  if (socialIcon) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        social: {
          ...properties.desktop.social,
          icon: socialIcon.id,
        },
      },
    };
  }

  var twitterIcon = _.get(properties, "desktop.twitter.icon", null);
  if (twitterIcon) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        twitter: {
          ...properties.desktop.twitter,
          icon: twitterIcon.id,
        },
      },
    };
  }

  var background = _.get(properties, "desktop.background");
  var media = _.get(background, "media");
  if (background && media) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        background: {
          ...background,
          media: media.id,
        },
      },
    };
  }

  return {
    ...widget,
    properties: properties,
  };
};

const AddSegmentButtonComponent = ({
  pageId,
  handleAddSegment,
  active,
  breakpoint,
}) => {
  const buttonRef = React.useRef(null);
  // Track visibility in a ref — only setState when values actually change
  const visibilityRef = useRef({ showTop: false, showBottom: false, showResize: false });

  const [state, setState] = useState({
    showTop: false,
    showBottom: false,
    showResize: false,
  });

  const [portalMountNode, setPortalMountNode] = useState(null);

  const editorWidgetScrollContainer = document.getElementsByClassName(
    "editor-page-wrapper"
  )[0];

  useLayoutEffect(() => {
    setPortalMountNode(document.getElementById(`page-${pageId}`));
  }, [pageId]);

  const updateVisibility = useCallback((showTop, showBottom) => {
    const prev = visibilityRef.current;
    if (prev.showTop === showTop && prev.showBottom === showBottom) return; // no-op, skip re-render

    visibilityRef.current = { showTop, showBottom, showResize: showTop || showBottom };
    setState((state) => ({
      ...state,
      showTop,
      showBottom,
      showResize: showTop || showBottom,
    }));
  }, []);

  const resetVisibility = useCallback(() => {
    const prev = visibilityRef.current;
    if (!prev.showTop && !prev.showBottom && !prev.showResize) return; // already hidden, skip

    visibilityRef.current = { showTop: false, showBottom: false, showResize: false };
    setState((state) => ({
      ...state,
      showResize: false,
      showTop: false,
      showBottom: false,
    }));
  }, []);

  useEffect(() => {
    const pageElement = document.getElementById(pageId);
    const overlay = document.getElementById(`hover-${pageId}`);
    if (!pageElement) return;

    const hoverZone = 100;

    const handleMouseMove = (element) => (e) => {
      const rect = element.getBoundingClientRect();
      const y = e.clientY - rect.top;

      if (y <= hoverZone) {
        updateVisibility(true, false);
      } else if (y >= rect.height - hoverZone) {
        updateVisibility(false, true);
      } else {
        updateVisibility(false, false);
      }
    };

    const handlePageMouseMove = handleMouseMove(pageElement);
    const handleOverlayMouseMove = overlay ? handleMouseMove(overlay) : null;

    pageElement.addEventListener("mousemove", handlePageMouseMove);
    pageElement.addEventListener("mouseleave", resetVisibility);

    if (overlay && handleOverlayMouseMove) {
      overlay.addEventListener("mousemove", handleOverlayMouseMove);
      overlay.addEventListener("mouseleave", resetVisibility);
    }

    return () => {
      pageElement.removeEventListener("mousemove", handlePageMouseMove);
      pageElement.removeEventListener("mouseleave", resetVisibility);

      if (overlay && handleOverlayMouseMove) {
        overlay.removeEventListener("mousemove", handleOverlayMouseMove);
        overlay.removeEventListener("mouseleave", resetVisibility);
      }
    };
  }, [pageId, updateVisibility, resetVisibility]);

  const pageElement = document.getElementById(pageId);
  const pageRect = pageElement?.getBoundingClientRect();
  if (!portalMountNode || !pageRect || !editorWidgetScrollContainer) return null;

  const pageHeight = pageRect.height;

  return (
    <Portal mountNode={portalMountNode}>
      {breakpoint == "desktop" && (
        <>
          <Button
            id="add-segment-button-top"
            name="add-segment-button-top"
            ref={buttonRef}
            size="sm"
            variant="primary"
            className={cx(styles.addSegmentButton, {
              [styles.hovering]: (state.showTop && active),
            })}
            onClick={() => {
              handleAddSegment("top");
            }}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Add Frame
          </Button>
          <Button
            id="add-segment-button-bottom"
            name="add-segment-button-bottom"
            ref={buttonRef}
            style={{
              top: `${pageHeight - 15}px`,
            }}
            size="sm"
            variant="primary"
            className={cx(styles.addSegmentButton, {
              [styles.hovering]: (state.showBottom && active),
            })}
            onClick={() => {
              handleAddSegment("bottom");
            }}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Add Frame
          </Button>
        </>
      )}
      <PageResizeHandle
        pageId={pageId}
        pageHeight={pageHeight}
        show={state.showResize}
      />
    </Portal>
  );
};

const PageResizeHandle = React.memo(({ pageId, pageHeight, show }) => {
  const dispatch = useDispatch();
  const dragStateRef = useRef(null);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const page = useWidgetSelector(editor, pageId);
  var widgetChildren = useWidgetChildrenSelector(editor, pageId) || [];
  const height =
    parseInt(_.get(page, `properties.${breakpoint}.layout.height`)) || 0;

  const onHeightChange = (newHeight) => {
    const layout = _.get(page, `properties.${breakpoint}.layout`) || {};
    dispatch(
      updateWidgetProperty(editor, pageId, breakpoint, {
        layout: {
          ...layout,
          height: `${newHeight}px`,
        },
      })
    );
  };

  const handleBottomResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    let maxBottom = 0;
    let maxChildId = null;

    widgetChildren.forEach((child) => {
      const transform = _.get(child, `properties.${breakpoint}.layout.transform.translate`, "");
      const visible = _.get(child, `properties.${breakpoint}.visible`, true);

      if (!visible) {
        return;
      }

      if (transform) {
        const parts = transform.split(",");
        const y = parseFloat(parts[1]?.replace("px", "").trim()) || 0;

        const element = document.getElementById(child?.uid);
        if (element) {
          const rect = element.getBoundingClientRect();
          const bottom = y + rect.height;

          if (bottom > maxBottom) {
            maxBottom = bottom;
            maxChildId = child?.uid;
          }
        }
      }
    });

    let boundary = maxBottom;
    actionGroup.start();

    dragStateRef.current = {
      startY: e.clientY,
      startHeight: height,
      onMouseMove: (moveEvent) => {
        if (!dragStateRef.current) return;

        const deltaY = moveEvent.clientY - dragStateRef.current.startY;
        let newHeight = dragStateRef.current.startHeight + deltaY;
        let prevHeight = newHeight;
        newHeight = Math.max(50, newHeight);

        if (boundary > 0 && newHeight < boundary) {
          newHeight = boundary;
        }

        const widget = document.getElementById(pageId);
        if (widget) {
          widget.style.height = `${newHeight}px`;
        }
        if (prevHeight >= 50) {
          onHeightChange(newHeight);
        }
      },
      onMouseUp: () => {
        if (!dragStateRef.current) return;

        const widget = document.getElementById(pageId);
        if (widget) {
          let finalHeight = parseInt(widget.style.height, 10) || 0;

          if (boundary > 0 && finalHeight < boundary) {
            finalHeight = boundary;
            widget.style.height = `${finalHeight}px`;
            onHeightChange(finalHeight);
          }
        }
        document.removeEventListener(
          "mousemove",
          dragStateRef.current.onMouseMove
        );
        document.removeEventListener("mouseup", dragStateRef.current.onMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        dragStateRef.current = null;

        actionGroup.end();
      },
    };

    document.addEventListener("mousemove", dragStateRef.current.onMouseMove);
    document.addEventListener("mouseup", dragStateRef.current.onMouseUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ns-resize";
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dragStateRef.current) {
        document.removeEventListener(
          "mousemove",
          dragStateRef.current.onMouseMove
        );
        document.removeEventListener("mouseup", dragStateRef.current.onMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    };
  }, []);

  const widgetVisibility = widgetChildren.map((child) => _.get(child, `properties.${breakpoint}.visible`, true)).join(",");

  useEffect(() => {
    let maxBottom = 0;

    widgetChildren.forEach((child) => {
      const transform = _.get(child, `properties.${breakpoint}.layout.transform.translate`, "");
      const visible = _.get(child, `properties.${breakpoint}.visible`, true);
      if (!visible) return;

      if (transform) {
        const parts = transform.split(",");
        const y = parseFloat(parts[1]?.replace("px", "").trim()) || 0;

        const element = document.getElementById(child?.uid);
        if (element) {
          const rect = element.getBoundingClientRect();
          const bottom = y + rect.height;
          if (bottom > maxBottom) {
            maxBottom = bottom;
          }
        }
      }
    });

    if (maxBottom > 0) {
      const widget = document.getElementById(pageId);
      if (widget) {
        const currentHeight = parseInt(widget.style.height, 10) || 0;
        if (currentHeight < maxBottom) {
          widget.style.height = `${maxBottom}px`;
          onHeightChange(maxBottom);
        }
      }
    }
  }, [widgetVisibility]);

  return (
    <Button
      id="resize-button"
      name="resize-button"
      onMouseDown={handleBottomResizeStart}
      style={{
        top: `${pageHeight - 15}px`,
        left: `70%`,
      }}
      size="sm"
      variant="primary"
      className={cx(styles.resizeButton, {
        [styles.hovering]: show,
      })}
    >
      <i className={cx(styles.icon, "cleverstory-stretch")} />
    </Button>
  );
});

export { PageWidget };